import { parseEgressConfig } from '@nadoutong/contracts';
import type {
  EgressAdmissionBlacklist,
  EgressAdmissionBlacklistRule,
  EgressAdmissionMode,
  EgressConfig,
} from '@nadoutong/contracts';
import { isOneOf } from './isOneOf.ts';

/**
 * The `require-cc-black` rule set, as the two console surfaces that edit it
 * need it (router#1609).
 *
 * Pure, and in `lib/` rather than beside the control, because the second
 * surface — the per-binding override in `KeyChannelEgressSheet` — needs the
 * composition rules below without importing a component.
 */

/**
 * In the runtime's evaluation order (`CC_BLACKLIST_RULE_ORDER` in
 * `packages/egress-runtime/src/cc-policy.ts`), which is what the checkbox list
 * renders in. An operator arming more than one rule needs to read "which of
 * these wins when a request trips two" off the order they are shown in, and
 * `firstPartyHealthProbe` is first because it is first at runtime.
 */
export const EGRESS_ADMISSION_BLACKLIST_RULES = [
  'firstPartyHealthProbe',
  'bareApiOnCcLane',
  'agentSdkOnCcLane',
  'missingCcBetaHeader',
] as const satisfies readonly (keyof EgressAdmissionBlacklist)[];
export type EgressAdmissionBlacklistRuleKey = (typeof EGRESS_ADMISSION_BLACKLIST_RULES)[number];

/**
 * Set or clear one rule, preserving the presence of the `blacklist` key
 * itself.
 *
 * `undefined` deletes the rule rather than writing a disabled marker: absence
 * is the off state in the contract, and `{action}` is the whole shape a rule
 * has — there is nowhere for an "off" spelling to live. Written rule by rule
 * rather than with a computed key so the result is an
 * `EgressAdmissionBlacklist` without a cast; a further rule needs a label and a
 * hint in the component regardless.
 */
export function blacklistWithRule(
  blacklist: EgressAdmissionBlacklist | null,
  rule: EgressAdmissionBlacklistRuleKey,
  value: EgressAdmissionBlacklistRule | undefined,
): EgressAdmissionBlacklist {
  const current: EgressAdmissionBlacklist = blacklist ?? {};
  const firstPartyHealthProbe =
    rule === 'firstPartyHealthProbe' ? value : current.firstPartyHealthProbe;
  const bareApiOnCcLane = rule === 'bareApiOnCcLane' ? value : current.bareApiOnCcLane;
  const agentSdkOnCcLane = rule === 'agentSdkOnCcLane' ? value : current.agentSdkOnCcLane;
  const missingCcBetaHeader = rule === 'missingCcBetaHeader' ? value : current.missingCcBetaHeader;
  return {
    ...(firstPartyHealthProbe !== undefined ? { firstPartyHealthProbe } : {}),
    ...(bareApiOnCcLane !== undefined ? { bareApiOnCcLane } : {}),
    ...(agentSdkOnCcLane !== undefined ? { agentSdkOnCcLane } : {}),
    ...(missingCcBetaHeader !== undefined ? { missingCcBetaHeader } : {}),
  };
}

export function armedBlacklistRules(
  blacklist: EgressAdmissionBlacklist | null | undefined,
): readonly EgressAdmissionBlacklistRuleKey[] {
  return EGRESS_ADMISSION_BLACKLIST_RULES.filter((rule) => blacklist?.[rule] !== undefined);
}

/**
 * Profile admission modes a binding-level blacklist may be composed over.
 *
 * Composing an override sets the document's mode to `require-cc-black`, so the
 * set is exactly the modes for which that flip refuses more without admitting
 * or refusing anything else:
 *
 * - `allow-any-supported` — the mode `require-cc-black` is defined against.
 *   `cc-admission-black-parity.test.ts` compares every admission-visible
 *   output of the two across both spellings of "no rule armed", all three lane
 *   modes and every client identity the header detector can return.
 *   Production's live cc-lane profiles run this mode, which is what makes the
 *   binding override usable at all.
 * - `require-cc-black` — already there; only the rules change.
 *
 * Everything else is deliberately excluded, and none of the exclusions is
 * cosmetic. `require-cc` would have its whitelist silently dropped.
 * `allow-anthropic` would widen to any supported client. `direct` would ARM
 * the sidecar recognize-step gate that `ccSidecarAdmissionGateConfigured`
 * leaves off for it, so a rule meant to refuse one named class would also
 * start refusing every client the gate does not recognize.
 *
 * An absent `admission` block is excluded for a different and smaller reason.
 * Since #1383 it resolves to `allow-any-supported` at every gate including the
 * sidecar one, so composing from it would not arm anything — but it would
 * write a whole admission document onto a profile that carried none, which is
 * more than the checkbox describes. An operator who wants the control sets
 * `allow-any-supported` explicitly first, and that edit is visible on its own.
 *
 * An operator ticking a checkbox has no reason to expect any of this, so the
 * control is withheld instead.
 */
const COMPOSABLE_PROFILE_ADMISSION_MODES = [
  'allow-any-supported',
  'require-cc-black',
] as const satisfies readonly EgressAdmissionMode[];

/**
 * Compose the full override document the write face accepts: the profile's own
 * config with the admission mode set to `require-cc-black` and the rule set
 * attached.
 *
 * A full document, not a fragment, because
 * `PUT .../key-buckets/:id/channels` runs
 * `serializeEgressConfig(parseEgressConfig(entry.egress_config_override))` and
 * takes nothing else — the runtime's deep merge of a partial fragment
 * (`packages/egress-runtime/src/config-resolve.ts`) has no write-side twin. The
 * consequence is that the result is a SNAPSHOT of the profile: a later profile
 * edit does not reach a binding carrying one. That is inherent to the write
 * face, so the UI's job is to make it visible rather than to hide it — see
 * `bindingBlacklistState`, which reports the divergence the moment it appears.
 *
 * `null` when no rule is armed: an override that only renames the mode changes
 * nothing an operator asked for (the parity suite is the proof) and would pin a
 * profile snapshot for no benefit, so the last untick removes the override
 * rather than storing an empty rule set.
 *
 * `null` also when the composition does not parse. Callers must therefore
 * distinguish the two by asking whether any rule was armed — a silent `null`
 * on a failed composition would read as "clear the override", which is the one
 * outcome this whole area exists to prevent. Returning rather than throwing is
 * deliberate: `bindingBlacklistState` calls this during render to reproduce a
 * stored override, and a throw there would take the sheet down instead of
 * degrading to the read-only branch.
 */
export function composeBindingBlacklistOverride(
  profileConfig: EgressConfig,
  rules: EgressAdmissionBlacklist | null,
): EgressConfig | null {
  const admission = profileConfig.admission;
  if (admission === undefined || rules === null || armedBlacklistRules(rules).length === 0) {
    return null;
  }
  try {
    // Round-tripped through the shared strict parser rather than trusted: this
    // document goes straight to a write face that parses it again, so a
    // composition that cannot parse has to be caught here, in the editor,
    // rather than as a save-time error naming a path the operator never typed.
    return parseEgressConfig({
      ...profileConfig,
      admission: { ...admission, mode: 'require-cc-black', blacklist: rules },
    });
  } catch {
    return null;
  }
}

/**
 * A stored binding override as its host holds it.
 *
 * Taken as this shape rather than as a bare `EgressConfig | null` on purpose.
 * `config === null` means the stored text is NOT a full document the write
 * face accepts — a partial fragment, which `resolveEgressConfig` still honours
 * at runtime by deep-merging it over the profile. That is a live egress policy
 * and it is emphatically not the same state as "this binding has no override",
 * but both spell `null` once the wrapper is dropped.
 *
 * They were dropped, once. `bindingBlacklistState(profile, override?.config ??
 * null)` made a fragment-bearing binding look editable, so ticking a rule
 * recomposed a fresh document over the fragment and destroyed it — and,
 * because the resulting override then parsed, it also fell out of the
 * "this save clears an override" set that forces the #1536 confirmation, so
 * the deletion was silent on both surfaces. Requiring the wrapper is what
 * stops that being expressible again.
 */
export interface StoredBindingOverride {
  readonly config: EgressConfig | null;
}

export type BindingBlacklistUnavailableReason =
  | 'no-profile'
  | 'not-cc-lane'
  | 'profile-mode'
  | 'override-fragment';

export type BindingBlacklistState =
  /** No control: this binding cannot carry a composed blacklist override. */
  | { readonly kind: 'unavailable'; readonly reason: BindingBlacklistUnavailableReason }
  /** Editable: the stored override (if any) is one this editor reproduces exactly. */
  | { readonly kind: 'editable'; readonly rules: EgressAdmissionBlacklist | null }
  /**
   * Read-only: a stored override exists that this editor cannot reproduce from
   * the profile. Either the profile changed after the override was saved, or
   * the override carries edits beyond the blacklist. The two are
   * indistinguishable from here — nothing records which profile revision a
   * snapshot was taken from — so neither is guessed, and the rules are shown
   * without an edit path that would overwrite the difference.
   */
  | { readonly kind: 'diverged'; readonly rules: EgressAdmissionBlacklist | null };

export function bindingBlacklistState(
  profileConfig: EgressConfig | null | undefined,
  override: StoredBindingOverride | null,
): BindingBlacklistState {
  if (profileConfig === null || profileConfig === undefined) {
    return { kind: 'unavailable', reason: 'no-profile' };
  }
  if (profileConfig.lane !== 'claude-code') {
    return { kind: 'unavailable', reason: 'not-cc-lane' };
  }
  const mode = profileConfig.admission?.mode;
  if (mode === undefined || !isOneOf(mode, COMPOSABLE_PROFILE_ADMISSION_MODES)) {
    return { kind: 'unavailable', reason: 'profile-mode' };
  }
  if (override === null) {
    return { kind: 'editable', rules: null };
  }
  if (override.config === null) {
    // A live partial fragment. Every edit path here replaces the whole
    // override document, so offering one would delete a policy that is in
    // force — and the replacement would parse, which is exactly what would
    // hide the deletion from the #1536 confirmation. Read-only is the only
    // honest answer while the fragment is stored; the sheet's own
    // unsupported-override warning already says a save will clear it.
    return { kind: 'unavailable', reason: 'override-fragment' };
  }
  const rules = override.config.admission?.blacklist ?? null;
  const reproduced = composeBindingBlacklistOverride(profileConfig, rules);
  // The second arm is the case where the override exists for a reason that is
  // not the blacklist. `composeBindingBlacklistOverride` answers `null` for "no
  // rule armed", which used to be unreachable here — an override with no rules
  // was cleared rather than stored — and stopped being so when #1760 gave the
  // document a second editable axis. Without it, arming a firewall reference
  // would put these checkboxes into the read-only branch, which is the opposite
  // of what the divergence warning is for: nothing has diverged, the profile
  // snapshot is verbatim.
  const reproduces = sameConfigDocument(reproduced, override.config)
    || (reproduced === null && sameConfigDocument(profileConfig, override.config));
  return reproduces ? { kind: 'editable', rules } : { kind: 'diverged', rules };
}

/**
 * Document equality by serialization.
 *
 * Sound because both sides are `parseEgressConfig` output: the parser builds
 * its result key by key in a fixed order, so two equal documents serialize
 * identically and a structural comparison would add nothing but a hand-rolled
 * deep-equal to maintain.
 */
function sameConfigDocument(a: EgressConfig | null, b: EgressConfig | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
